import * as React from 'react'
import { ReactElement, useContext, useMemo, useState } from 'react'
import { DataPromise, Destination } from '../../context/AxiosContext'
import { IFileDto } from '../../config/fileConfig'
import { AxiosResponse } from 'axios'
import useAxios from '../../hooks/useAxios'
import { BaseOverviewFilterType } from '../BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../BaseOverview/BaseSortMenu'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    parseFilterValuesToParams,
    useOverviewStyles,
} from '../BaseOverview/BaseOverview'
import { EntityDto } from '../../config/appConfig'
import { SortDirection } from '../../config/settingConfig'
import { makeStyles, mergeClasses, Text } from '@fluentui/react-components'
import useUsers from '../../hooks/useUsers'
import PersonAvatar from '../../components/PersonAvatar'
import FormattedDateTime from '../../components/Date/FormattedDateTime'
import { UserListItemDto } from '../../config/userConfig'
import useFilePages from '../../hooks/file/useFilePages'
import { useTranslation } from 'react-i18next'
import useAttributes, { AttributeContextType, AttributeViewType } from '../../hooks/useAttributes'
import { parseDateTime } from '../../helpers/dateHelper'
import AuthorizationContext from '../../context/AuthorizationContext'

const useItemStyles = makeStyles({
    listAuditTrailItem: {
        display: 'grid',
        gridTemplateColumns: '28px auto 65px',
    },
})

type Props = {
    entity: DataPromise<IFileDto>
}

type AuditTrailDto = {
    PartitionKey: string
    RowKey: string
    Timestamp: string
    User: string
    Path: string
}

type ExtendedAuditTrailDto = AuditTrailDto &
    EntityDto & {
        hostKey: string
        fileId: string
        user?: UserListItemDto
        page?: string
    }

type AuditTrailFilterType = BaseOverviewFilterType & {
    users?: string[]
}

type AuditTrailSorting = {
    id: string
    'user.fullName': string
    timestamp: string
}

type AuditTrailSortingType = BaseOverviewSortingType<AuditTrailSorting>

const headerSortingItems: AuditTrailSortingType[] = [
    { property: ['timestamp', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc' },
    {
        property: ['timestamp', 'id'],
        direction: SortDirection.Descending,
        default: true,
        descriptionKey: 'sorting.datedesc',
    },
    { property: ['user.fullName', 'id'], direction: SortDirection.Ascending, descriptionKey: 'sorting.useraz' },
    { property: ['user.fullName', 'id'], direction: SortDirection.Descending, descriptionKey: 'sorting.userza' },
]

const ATTRIBUTES_KEY = 'attribute'

export function AuditTrailListItem(props: BaseOverviewLineItemProps<ExtendedAuditTrailDto, AuditTrailFilterType, AuditTrailSortingType>) {
    const overviewStyles = useOverviewStyles()

    return (
        <>
            <PersonAvatar name={props.item.user.fullName} color={'colorful'} size={24} />
            <div className={mergeClasses(overviewStyles.flexColumn)}>
                <Text size={200}>{props.item.page}</Text>
            </div>
            <div className={overviewStyles.alignEnd}>{props.item.Timestamp && <FormattedDateTime date={props.item.Timestamp} />}</div>
        </>
    )
}

export default function AuditTrailOverview(props: Props): ReactElement {
    const { attributeCategories } = useAttributes(AttributeViewType.View, [AttributeContextType.File])
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()
    const { getUserById } = useUsers()

    const { pages } = useFilePages(props.entity.data?.id)

    const [count, setCount] = useState<number>(1000)
    const defaultFilterValues = useMemo<AuditTrailFilterType>(
        () => ({
            users: [],
            query: '',
        }),
        [],
    )

    const countItems = (): Promise<BaseOverviewCount> => {
        return Promise.resolve({ total: count })
    }

    const loadItems = (
        _: number,
        filterValues?: AuditTrailFilterType,
        sortingValues?: AuditTrailSortingType,
        start?: number,
        size?: number,
    ): Promise<ExtendedAuditTrailDto[]> => {
        return axios
            .get(`/audit-trail/${currentHost?.RowKey}/file/${props.entity.data?.id}`, {
                params: { ...parseFilterValuesToParams(filterValues, sortingValues, start, size) },
                baseURL: process.env.API_HOST,
                headers: { Destination: Destination.Hammock },
            })
            .then((response: AxiosResponse<AuditTrailDto[]>): ExtendedAuditTrailDto[] =>
                (response?.data || [])
                    .sort((a, b) => {
                        return parseDateTime(b.Timestamp).toMillis() - parseDateTime(a.Timestamp).toMillis()
                    })
                    .map(item => {
                        const hostKey = item.PartitionKey.split('\|')[0]
                        const fileId = item.PartitionKey.split('\|')[1]

                        let pathSegment = item.Path.replaceAll(/[a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12}/g, '')
                        pathSegment = pathSegment.replaceAll('//', '/')
                        pathSegment = pathSegment.replaceAll('/file/', '')
                        pathSegment = pathSegment.replaceAll('/', '.') || 'document'
                        const pathKey = pathSegment.replace(/[-_]/, '')

                        const pageKey = pages.find(filePage => filePage.route === pathKey || filePage.key === pathKey)?.key || pathKey
                        let title = pageKey.includes(ATTRIBUTES_KEY) ? translate('file.pivot.attribute.title') : pageKey
                        const defaultCat = translate('file.edit.form.attributes.label')
                        title = pageKey.includes(ATTRIBUTES_KEY)
                            ? ([...attributeCategories, defaultCat].find(
                                  cat => cat?.toLowerCase().localeCompare(pathSegment.match(/attribute(s?)-(.*)/)?.[2]) === 0,
                              ) ?? translate('file.pivot.attribute.title'))
                            : translate(`file.pivot.${title.toLowerCase()}.title`)
                        return {
                            ...item,
                            id: item.RowKey,
                            hostKey: hostKey,
                            fileId: fileId,
                            user: getUserById(item.User),
                            page: title,
                            Timestamp: parseDateTime(item.Timestamp, { zone: 'UTC' }).toJSON(),
                        }
                    }),
            )
    }

    if (props.entity.loading || props.entity.rejected) {
        return <></>
    }

    return (
        <BaseOverview<ExtendedAuditTrailDto, AuditTrailFilterType, AuditTrailSortingType>
            defaultFilterValues={defaultFilterValues}
            batchSizeItems={1000}
            countItems={countItems}
            loadItems={loadItems}
            listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
            onLoadItems={(_column, pages, items) => {
                if (items.length % 1000 === 0 && items.length !== pages.length) {
                    setCount(items.length + 1000)
                }
            }}
            listItemContent={props => <AuditTrailListItem {...props} />}
            listItemClassnames={() => [itemStyles.listAuditTrailItem]}
            headerSortingItems={headerSortingItems}
        />
    )
}
